// eslint-disable-next-line @typescript-eslint/no-unused-vars

import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { merge } from 'lodash';

import { Events } from '../constants';
import { TokenFormFieldConfig, TokenFormProps } from '../@types';
import { DefaultTheme } from '../theme/default';
import { log } from '../utils/logger';

import { PaymentCardForm as PaymentCardFormComponent } from './PaymentCardForm';
// import { CSSReset } from './CSSReset';

// TODO: delete because moved into hook
export const defaultFormConfig: TokenFormFieldConfig = {
  cardFields: {
    cardholderName: {
      required: true,
    },
    cardNumber: {
      required: true,
    },
    expiry: {
      required: true,
    },
    cvv: {
      required: true,
    },
  },
  // contactFields: {
  //   firstName: {
  //     required: true,
  //   },
  //   lastName: {
  //     required: true,
  //   },
  // },
  // billingFields: {
  //   billingDocumentType: {
  //     required: true,
  //   },
  //   billingDocumentNumber: {
  //     required: true,
  //   },
  //   billingAddress1: {
  //     required: true,
  //   },
  //   billingAddress2: {
  //     required: false,
  //   },
  //   billingCountry: {
  //     required: true,
  //   },
  //   billingState: {
  //     required: false,
  //   },
  //   billingEmail: {
  //     required: false,
  //   },
  // },
};

const scopedCss = true; // TODO: shold it be moved into .env or prop?

const dispatchEvent = (event: string, data: any) => {
  window.dispatchEvent(
    new CustomEvent(event, {
      // composed: true,
      bubbles: true,
      detail: {
        data,
      },
    }),
  );
};

// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-unused-vars
export let submit = async (callback?: () => unknown): Promise<any> => {
  log('[@cloudbeds/payment-tokenization] form submitted.');
  callback?.();
};
// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-unused-vars
export let validate = async (
  callback?: () => unknown,
): Promise<{ errors?: any; valid: any }> => {
  log('[@cloudbeds/payment-tokenization] form validated.');
  callback?.();
  return {
    valid: false,
  };
};

export const CardTokenForm = forwardRef(
  (
    // TODO: cardBrands should be fetched from settings, remove when implemented
    { lang = 'en', propertyId, cardBrands, ...props }: TokenFormProps,
    fwdRef: any,
  ) => {
    const ref = useRef();
    const apiTokenConfig = {
      apiEnv: props.env || 'local',
      apiToken: props.apiToken || '',
    };

    let submitCallback = props?.onSubmit;
    let validateCallback = props?.onValidate;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const theme = useMemo(
      () => extendTheme(merge({}, DefaultTheme, props.theme || {})),
      [props.theme],
    );

    const validateInternalForm = async () => {
      const validateFunc = (ref.current as any)?.validate;
      if (validateFunc) return validateFunc();
      return true;
    };

    const getInternalFormState = () => {
      const getStateFunc = (ref.current as any)?.getFormState;
      if (getStateFunc) return getStateFunc();
      return null;
    };

    const submitInternalForm = async () => {
      const isValid = await validateInternalForm();
      if (isValid) {
        const submitFunc = (ref.current as any)?.submit;
        if (submitFunc) return submitFunc();
        return true;
      }
      log('[@cloudbeds/payment-tokenization] Form is not valid.');
      return false;
    };

    submit = async callbackFn => {
      /* we overwrite default submit handler (extracted from props) */
      if (callbackFn) submitCallback = callbackFn;
      return submitInternalForm();
    };

    validate = async callbackFn => {
      /* we overwrite default validate handler (extracted from props) */
      if (callbackFn) validateCallback = callbackFn;
      const isValid = await validateInternalForm();
      return {
        valid: isValid,
        ...(!isValid && { errors: getInternalFormState()?.errors }),
      };
    };

    const onLoadStart = () => {
      props.onLoadStart?.();
      dispatchEvent(Events.LoadStart, {});
    };

    const onLoadEnd = () => {
      props.onLoadEnd?.();
      dispatchEvent(Events.LoadEnd, {});
    };

    const onFormSubmit = (data: any) => {
      submitCallback?.(data);
      dispatchEvent(Events.Submit, data);
    };

    const onFormTokenize = (data: any) => {
      props.onTokenize?.(data);
      dispatchEvent(Events.Tokenize, data);
    };

    const onFormChange = (data: any) => {
      props.onChange?.(data);
      dispatchEvent(Events.Change, data);
    };

    const onFormError = (data: any) => {
      props.onError?.(data);
      dispatchEvent(Events.Error, data);
    };

    const onFormValidate = (data: any) => {
      validateCallback?.(data);
      dispatchEvent(Events.Validate, data);
    };

    /**
     * TODO: this should be removed since now we support normal flow.
     * @deprecated
     */
    useImperativeHandle(fwdRef, () => ({
      // @deprecated
      hello: () => {
        log('[@cloudbeds/payment-tokenization] Hello');
      },
      // @deprecated
      submit: submitInternalForm,
      // @deprecated
      validate: validateInternalForm,
    }));

    return (
      <ChakraProvider theme={theme} resetCSS={!scopedCss}>
        {/* {scopedCss && <CSSReset scope=".token-form-scope" />} */}
        <div className="token-form-scope">
          <PaymentCardFormComponent
            fields={merge({}, defaultFormConfig, props.fields)}
            validateOnBlur={true}
            ref={ref}
            lang={lang}
            propertyId={propertyId}
            env={apiTokenConfig.apiEnv}
            apiToken={apiTokenConfig.apiToken}
            gateway={{}}
            labels={props.labels}
            canEditGuest={props.canEditGuest}
            defaultValues={props.defaultValues}
            onSubmitHandler={onFormSubmit}
            onErrorHandler={onFormError}
            onChangeHandler={onFormChange}
            onValidateHandler={onFormValidate}
            onTokenizeHandler={onFormTokenize}
            onLoadStart={onLoadStart}
            onLoadEnd={onLoadEnd}
            cardBrands={cardBrands}
          />
        </div>
      </ChakraProvider>
    );
  },
);
